<template lang="pug">
    AiToolsTabs.prompt-template-list
        el-card.contains-table(shadow="always")
            .error(v-if="error")
                template(v-if="error.code")
                    Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
                template(v-else)
                    Error(:message="$t('Something went wrong!')" :code="501")
            .templates-table(v-else)
                paginated-items(
                    scope="ex"
                    type="generative_media_prompt_template"
                    :loading="loading"
                    :items="promptTemplatesPaginated.items"
                    :filters="filters"
                    :show-filters="showFilters"
                    :variables="promptTemplatesPaginated.variables"
                    :all-columns="promptTemplatesPaginated.allColumns"
                    :active-columns="promptTemplatesPaginated.activeColumns"
                    :page-info="promptTemplatesPaginated.pageInfo"
                    :sort="sort"
                    @pagination-size-change="handleSizeChange"
                    @pagination-current-change="handleCurrentChange"
                    @show-table-columns="handleColsShow"
                    @change-filter="handleFilterInputChange"
                    @change-sort="handleSortInputChange"
                    @click="handleClick"
                )
                    template(v-slot:name="{ item, value }")
                        .template-name
                            img.template-name__thumb(v-if="item.testFile && item.testFile.url" :src="item.testFile.url")
                            span.template-name__thumb.template-name__thumb--empty(v-else)
                                i.el-icon-fa-wand-magic-sparkles.fas
                            span {{ value }}
                    template(v-slot:intent="{ value }")
                        span {{ truncate(value, 90) }}
                    template(v-slot:resolved_context="{ value }")
                        el-tag(
                            size="mini"
                            :type="value === 'product' ? 'warning' : 'info'"
                        ) {{ value === 'product' ? $t('Product image') : $t('General') }}
                    template(v-slot:actions="{ item }")
                        el-tooltip(:content="$t('Delete')" placement="top")
                            el-button(
                                plain
                                circle
                                size="mini"
                                type="danger"
                                icon="el-icon-fa-trash-alt fas"
                                @click.stop.prevent="openDeleteDialog(item)"
                            )
                    template(slot="top")
                        el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                            .left
                                el-button(
                                    type="primary"
                                    size="mini"
                                    icon="el-icon-fa-plus fas"
                                    @click="openTemplateDrawer()"
                                ) {{ $t('New Template') }}
                            .right.hide-on-empty
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-filter fas"
                                    @click="toggleFilterVisibility"
                                    :disabled="disable.showFilters || hasFilters"
                                )
                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                    span(v-else) {{ $t('Show filters') }}

        TemplateDrawer(
            :visible.sync="templateDrawerVisible"
            :template="editingTemplate"
            @saved="onTemplateSaved"
        )

        el-dialog(
            :title="$t('Delete Template')"
            :visible.sync="deleteDialog.visibility"
            :close-on-click-modal="false"
            top="10vh"
            width="460px"
        )
            .body(v-loading="deleteDialog.loading")
                p {{ $t('Do you really want to delete the template?') }}
            template(slot="footer")
                .modal-footer
                    .left
                    .right
                        el-button(
                            size="mini"
                            icon="el-icon-fa-times fas"
                            :disabled="deleteDialog.loading"
                            @click="deleteDialog.visibility = false"
                        ) {{ $t('Cancel') }}
                        el-button(
                            size="mini"
                            type="danger"
                            icon="el-icon-fa-trash-alt fas"
                            :loading="deleteDialog.loading"
                            @click="confirmDelete"
                        ) {{ $t('Delete') }}
</template>

<i18n>
{
    "en": {},
    "de": {
        "New Template": "Neue Vorlage",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Product image": "Produktbild",
        "General": "Allgemein",
        "Delete": "Löschen",
        "Delete Template": "Vorlage löschen",
        "Do you really want to delete the template?": "Möchten Sie die Vorlage wirklich löschen?",
        "Cancel": "Abbrechen",
        "yyyy-MM-dd": "dd.MM.yyyy",
        "Template deleted successfully": "Vorlage erfolgreich gelöscht",
        "Template could not be deleted": "Vorlage konnte nicht gelöscht werden",
        "Prompt templates could not be fetched.": "Prompt-Vorlagen konnten nicht abgerufen werden.",
        "An error occurred": "Ein Fehler ist aufgetreten"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';

import pinia from '@/pinia';
import { useSetupStore, usePromptTemplateStore } from '@/stores';

import PaginatedItems from '@/components/PaginatedItems';
import Error from '@/components/Error';
import AiToolsTabs from '@/components/AiToolsTabs';
import TemplateDrawer from './TemplateDrawer';

export default {
    name: 'PromptTemplateList',

    components: {
        PaginatedItems,
        Error,
        AiToolsTabs,
        TemplateDrawer
    },

    data() {
        return {
            error: false,
            disable: {
                showFilters: false
            },
            templateDrawerVisible: false,
            editingTemplate: null,
            deleteDialog: {
                visibility: false,
                loading: false,
                template: null
            }
        };
    },

    computed: {
        showFilters() {
            return this.hasFilters || _.get(this.promptTemplatesPaginated, 'showFilters') || false;
        },
        hasFilters() {
            return !_.isEmpty(this.filters);
        },
        filters() {
            return _.cloneDeep(this.promptTemplatesPaginated.filters);
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        promptTemplateStore() {
            return usePromptTemplateStore();
        },
        loading() {
            return this.promptTemplateStore.loading;
        },
        promptTemplatesPaginated() {
            return this.promptTemplateStore.promptTemplatesPaginated;
        },
        sort() {
            const variables = this.promptTemplatesPaginated.variables;
            const defaultSort = this.promptTemplatesPaginated.defaultSort || { field: 'created_at', direction: 'desc' };
            const order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            const direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction: direction };
        },
        setupStore() {
            return useSetupStore();
        }
    },

    watch: {
        activeCms: {
            immediate: true,
            handler(cms) {
                const cmsId = _.get(cms, 'id');
                this.promptTemplateStore.setCmsId(cmsId);
                if (cmsId) this.refetch();
            }
        }
    },

    methods: {
        truncate(text, length) {
            if (!text) return '';
            return text.length > length ? text.substring(0, length) + '...' : text;
        },
        handleClick(item) {
            this.openTemplateDrawer(item);
        },
        handleSizeChange(itemsPerPage) {
            this.promptTemplateStore.updateLoading(true);
            this.promptTemplateStore.defineItemsPerPage(itemsPerPage);
            this.refetch();
        },
        handleCurrentChange(currentPage) {
            this.promptTemplateStore.updateLoading(true);
            this.promptTemplateStore.defineCurrentPage(currentPage);
            this.refetch();
        },
        handleColsShow(visibleColumns) {
            this.promptTemplateStore.defineActiveColumns(visibleColumns);
            apolloClient.clearStore();
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.promptTemplateStore.defineFilterValue({ key, value });
            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        handleSortInputChange(key, value) {
            this.promptTemplateStore.defineSorting({ key, value });
            this.refetch();
        },
        toggleFilterVisibility() {
            const visible = this.showFilters;
            const value = !visible;

            this.disable.showFilters = true;
            this.promptTemplateStore.defineFilterVisibility(value);
            this.setupStore.saveFilterVisibility({ scope: 'ex', key: 'generative_media_prompt_template', value })
                .finally(() => { this.disable.showFilters = false; });
        },
        refetch() {
            this.promptTemplateStore.updateLoading(true);
            this.promptTemplateStore.fetchPromptTemplatesPaginated()
                .then(() => this.promptTemplateStore.updateLoading(false))
                .catch(err => {
                    const { graphQLErrors } = err;

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    let notify = true;
                    switch (code) {
                        case 401:
                            notify = false;
                            this.error = { code, message: 'You are not authorized' };
                            break;
                        case 403:
                            notify = false;
                            this.error = { code, message: 'You are not allowed to be here' };
                            break;
                        default:
                            this.error = true;
                            break;
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Prompt templates could not be fetched.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                    this.promptTemplateStore.updateLoading(false);
                    console.error(err);
                });
        },
        debouncedRefetch: _.debounce(function() {
            this.promptTemplateStore.updateLoading(true);
            this.refetch();
        }, useSetupStore(pinia).debounce || 300),
        openTemplateDrawer(template = null) {
            this.editingTemplate = template ? _.cloneDeep(template) : null;
            this.templateDrawerVisible = true;
        },
        onTemplateSaved() {
            this.refetch();
        },
        openDeleteDialog(template) {
            this.deleteDialog.template = template;
            this.deleteDialog.visibility = true;
        },
        confirmDelete() {
            const template = this.deleteDialog.template;
            if (!template) return;

            this.deleteDialog.loading = true;
            this.promptTemplateStore.deletePromptTemplate(template.id)
                .then(() => {
                    this.$notify.success({
                        title: this.$t('Template deleted successfully'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                    this.deleteDialog.visibility = false;
                    this.refetch();
                })
                .catch(() => {
                    this.$notify.error({
                        title: this.$t('An error occurred'),
                        message: this.$t('Template could not be deleted'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .finally(() => {
                    this.deleteDialog.loading = false;
                });
        }
    }
};
</script>

<style lang="stylus" scoped>
.template-name
    display flex
    align-items center
    gap 10px

    &__thumb
        flex 0 0 34px
        width 34px
        height 34px
        border-radius 6px
        object-fit cover
        background #f4f5f7

        &--empty
            display inline-flex
            align-items center
            justify-content center
            color #c0c4cc
            font-size 14px
</style>
